
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionData, SubscriptionUpdateData, TimeSlot } from "@/types/subscription";

/**
 * Functions for fetching subscription data
 */
const fetchService = {
  /**
   * Fetch subscription data for a user
   */
  async getSubscriptionData(userId: string): Promise<SubscriptionData | null> {
    try {
      // Get the current subscription if any
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from("subscriptions")
        .select("id, status, timezone")
        .eq("profile_id", userId)
        .eq("status", "active")
        .maybeSingle();
      
      if (subscriptionError) throw subscriptionError;
      
      // If no subscription found, return empty data
      if (!subscriptionData) {
        return {
          subscription: null,
          categories: [],
          timeSlots: []
        };
      }
      
      // Get categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("subscription_categories")
        .select("category_id")
        .eq("subscription_id", subscriptionData.id);
        
      if (categoriesError) throw categoriesError;
      
      // Get time slots
      const { data: timeSlotsData, error: timeSlotsError } = await supabase
        .from("delivery_times")
        .select("id, time_of_day, category_id")
        .eq("subscription_id", subscriptionData.id);
        
      if (timeSlotsError) throw timeSlotsError;
      
      // Format data for return
      return {
        subscription: subscriptionData,
        categories: categoriesData.map(c => c.category_id),
        timeSlots: timeSlotsData.map(ts => ({
          id: ts.id,
          time: ts.time_of_day.substring(0, 5), // Convert "HH:MM:SS" to "HH:MM"
          categoryId: ts.category_id
        }))
      };
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      throw error;
    }
  }
};

/**
 * Functions for updating subscription data
 */
const updateService = {
  /**
   * Update or create a subscription for a user
   */
  async updateSubscription(userId: string, data: SubscriptionUpdateData, skipNotification: boolean = false): Promise<boolean> {
    try {
      // Check if profile exists
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .single();
        
      if (profileError) throw profileError;
      
      // Get existing subscription
      const { data: existingSubscription, error: subError } = await supabase
        .from("subscriptions")
        .select("id, status")
        .eq("profile_id", userId)
        .eq("status", "active")
        .maybeSingle();
        
      if (subError) throw subError;
      
      // Handle subscription creation or update
      const subscriptionId = await this._createOrUpdateSubscription(
        userId, 
        existingSubscription, 
        data.timezone
      );
      
      // Update categories and time slots
      await this._updateCategoriesAndTimeSlots(
        subscriptionId, 
        data.selectedCategories, 
        data.timeSlots
      );
      
      // Send notification if needed
      if (!skipNotification) {
        await this._sendSubscriptionNotification(
          userId, 
          subscriptionId, 
          data.timeSlots, 
          !existingSubscription
        );
      }
      
      return true;
    } catch (error) {
      console.error("Error updating subscription:", error);
      throw error;
    }
  },
  
  /**
   * Create or update a subscription record
   * @private
   */
  async _createOrUpdateSubscription(
    userId: string, 
    existingSubscription: any, 
    timezone: string
  ): Promise<string> {
    if (existingSubscription) {
      // Update existing subscription
      const { error: updateError } = await supabase
        .from("subscriptions")
        .update({
          timezone: timezone,
          updated_at: new Date().toISOString()
        })
        .eq("id", existingSubscription.id);
        
      if (updateError) throw updateError;
      return existingSubscription.id;
    } else {
      // Create new subscription
      const { data: newSubscription, error: createError } = await supabase
        .from("subscriptions")
        .insert({
          profile_id: userId,
          timezone: timezone,
          status: "active",
          confirmation_status: "confirmed", // Skip confirmation step
        })
        .select("id")
        .single();
        
      if (createError) throw createError;
      
      // Update profile status
      await supabase
        .from("profiles")
        .update({
          subscription_status: "active",
          updated_at: new Date().toISOString()
        })
        .eq("id", userId);
      
      return newSubscription.id;
    }
  },
  
  /**
   * Update subscription categories and time slots
   * @private
   */
  async _updateCategoriesAndTimeSlots(
    subscriptionId: string, 
    categories: string[], 
    timeSlots: TimeSlot[]
  ): Promise<void> {
    // Clear existing categories and time slots
    await supabase
      .from("subscription_categories")
      .delete()
      .eq("subscription_id", subscriptionId);
      
    await supabase
      .from("delivery_times")
      .delete()
      .eq("subscription_id", subscriptionId);
    
    // Insert new categories
    if (categories.length > 0) {
      const categoryInserts = categories.map(categoryId => ({
        subscription_id: subscriptionId,
        category_id: categoryId
      }));
      
      const { error: insertCatError } = await supabase
        .from("subscription_categories")
        .insert(categoryInserts);
        
      if (insertCatError) throw insertCatError;
    }
    
    // Insert new time slots
    if (timeSlots.length > 0) {
      const timeSlotInserts = timeSlots.map(slot => ({
        subscription_id: subscriptionId,
        time_of_day: slot.time,
        category_id: slot.categoryId
      }));
      
      const { error: insertTimesError } = await supabase
        .from("delivery_times")
        .insert(timeSlotInserts);
        
      if (insertTimesError) throw insertTimesError;
    }
  },
  
  /**
   * Send subscription notification via edge function
   * @private
   */
  async _sendSubscriptionNotification(
    userId: string, 
    subscriptionId: string, 
    timeSlots: TimeSlot[], 
    isNewSubscription: boolean
  ): Promise<void> {
    // Get subscriber phone number
    const { data: subscriberData, error: subscriberError } = await supabase
      .from("profiles")
      .select("phone_number")
      .eq("id", userId)
      .maybeSingle();
      
    if (subscriberError) throw subscriberError;
    
    if (subscriberData?.phone_number) {
      // Send subscription notification via edge function
      await supabase.functions.invoke("send-subscription-notification", {
        body: {
          subscriptionId,
          phoneNumber: subscriberData.phone_number,
          timeSlots: timeSlots,
          isNewSubscription
        }
      });
    }
  }
};

/**
 * Functions for canceling/deleting subscriptions
 */
const cancelService = {
  /**
   * Cancel a subscription
   */
  async cancelSubscription(userId: string, subscriptionId: string): Promise<boolean> {
    try {
      // Update subscription status
      const { error: updateError } = await supabase
        .from("subscriptions")
        .update({
          status: "cancelled",
          updated_at: new Date().toISOString()
        })
        .eq("id", subscriptionId)
        .eq("profile_id", userId);
        
      if (updateError) throw updateError;
      
      // Update profile status
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          subscription_status: "inactive",
          updated_at: new Date().toISOString()
        })
        .eq("id", userId);
        
      if (profileError) throw profileError;
      
      return true;
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      throw error;
    }
  },
  
  /**
   * Delete a subscriber and all their related data
   */
  async deleteSubscriber(userId: string): Promise<boolean> {
    try {
      // Delete profile (cascade will delete subscriptions, etc.)
      const { error } = await supabase
        .from("profiles")
        .delete()
        .eq("id", userId);
        
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error("Error deleting subscriber:", error);
      throw error;
    }
  }
};

/**
 * Combined service for managing subscriptions
 */
export const subscriptionService = {
  fetchSubscriptionData: fetchService.getSubscriptionData,
  updateSubscription: updateService.updateSubscription.bind(updateService),
  cancelSubscription: cancelService.cancelSubscription,
  deleteSubscriber: cancelService.deleteSubscriber
};
